import { useTranslation } from 'react-i18next';
import type { SurveyInstance, HomeQuestion } from '@/types/survey.types';
import { useInlineQuiz, isMatrixQuestion, serializeAnswers, type QuizAnswer, type EffectiveQuestion } from '../hooks/useInlineQuiz';
import { InitialsAvatar } from '@/components/common';
import { MatrixBody } from './MatrixBody';
import { QuestionBody } from './QuestionBody';

interface ActiveSurveyInlineProps {
  instance: SurveyInstance;
  totalAnswered: number;
  totalSurveys: number;
  stackCount: number;
  onOmit: () => void;
  onComplete: (answers: Record<string, string | number>) => void;
  hasNext: boolean;
}

function formatTimeRange(isoDate: string, durationMinutes: number): string {
  const start = new Date(isoDate);
  const end = new Date(start.getTime() + durationMinutes * 60000);
  const fmt = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fmt(start)} – ${fmt(end)}`;
}

// ─── Question meta (badge + fundamental strip) ────────────────────────────────

function QuestionMeta({ eq, qNum }: { eq: EffectiveQuestion; qNum: number }) {
  const { participant } = eq;
  return (
    <div className="home-q-meta">
      <span className="home-q-num">{qNum}</span>
      {participant && (
        <span className="home-q-participant-badge">
          {/* Avatar suelto (no hay posición en una lista): el color se deriva del propio nombre. */}
          <InitialsAvatar name={participant} baseClass="home-q-participant-av" seed={participant} />
          <span className="home-q-participant-name">{participant}</span>
        </span>
      )}
    </div>
  );
}

function FundamentalStrip({ q }: { q: HomeQuestion }) {
  if (!q.fundamentalName && !q.behaviorLabel) return null;
  return (
    <div className="home-q-fund-strip">
      {q.fundamentalName && (
        <span className="home-q-fund-chip">
          <i className="fas fa-layer-group" />{q.fundamentalName}
        </span>
      )}
      {q.behaviorLabel && (
        <>
          <i className="fas fa-chevron-right home-q-fund-sep" />
          <span className="home-q-fund-chip home-q-beh-chip">
            <i className="fas fa-seedling" />{q.behaviorLabel}
          </span>
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ActiveSurveyInline({
  instance, totalAnswered, totalSurveys, stackCount, onOmit, onComplete,
}: ActiveSurveyInlineProps) {
  const { t } = useTranslation();
  const {
    effectiveQuestions, answers, participants,
    setAnswer, setMatrixAnswer, canAdvance,
    nextPage, prevPage, isLastPage, page, totalPages, pageStart, visibleQuestions,
  } = useInlineQuiz(instance);

  const circumference = 2 * Math.PI * 18;
  const fillLength = circumference * (totalSurveys > 0 ? totalAnswered / totalSurveys : 0);
  const timeRange = formatTimeRange(instance.meetingDate, instance.durationMinutes);

  return (
    <div className="home-stories">
      <div className="home-stories-stack">
        {stackCount >= 2 && <div className="home-story-card-bg" />}
        {stackCount >= 1 && <div className="home-story-card-bg" />}

        <div className="home-story-card active">

          {/* Top row */}
          <div className="home-story-top">
            <div className="home-story-meeting">
              <div className="home-story-icon-box" aria-hidden="true"><i className="fas fa-comments" /></div>
              <div className="home-story-meeting-text">
                <div className="home-story-name">{instance.meetingTitle}</div>
                <div className="home-story-meta-row">
                  {timeRange} · {instance.participants.length} {t('survey.participants')}
                </div>
              </div>
            </div>
            <div className="home-story-top-right">
              <div className="home-stories-progress">
                <svg viewBox="0 0 44 44">
                  <circle className="home-stories-progress-bg" cx="22" cy="22" r="18" />
                  <circle className="home-stories-progress-fill" cx="22" cy="22" r="18"
                    strokeDasharray={`${fillLength} ${circumference}`} />
                </svg>
                <span className="home-stories-progress-text">{totalAnswered}/{totalSurveys}</span>
              </div>
              <button className="home-stories-skip-btn" onClick={onOmit} type="button">
                <i className="fas fa-forward" aria-hidden="true" />{t('survey.omit')}
              </button>
            </div>
          </div>

          {/* Page indicator */}
          {totalPages > 1 && (
            <div className="home-q-page-indicator">
              <span className="home-q-page-text">
                {t('survey.questionsRange', {
                  from: pageStart + 1, to: pageStart + visibleQuestions.length,
                  total: effectiveQuestions.length,
                  defaultValue: 'Preguntas {{from}}–{{to}} de {{total}}',
                })}
              </span>
              <div className="home-q-page-dots">
                {Array.from({ length: totalPages }, (_, i) => (
                  <span key={i} className={`home-q-page-dot${i === page ? ' active' : ''}`} />
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          <div className="home-questions">
            {visibleQuestions.map((eq, i) => {
              const eIdx = pageStart + i;
              const ans = answers[eIdx];

              return (
                <div key={eq.effectiveId} className="home-q">
                  <QuestionMeta eq={eq} qNum={eIdx + 1} />
                  <FundamentalStrip q={eq.question} />
                  <p className="home-q-text">{t(eq.question.text)}</p>

                  {isMatrixQuestion(eq.question) ? (
                    <MatrixBody
                      q={eq.question}
                      eIdx={eIdx}
                      matrixAnswers={ans as QuizAnswer[]}
                      participants={participants}
                      setMatrixAnswer={setMatrixAnswer}
                    />
                  ) : (
                    <QuestionBody
                      q={eq.question}
                      eIdx={eIdx}
                      selected={ans as QuizAnswer}
                      setAnswer={setAnswer}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="home-story-actions">
            {page > 0 ? (
              <button type="button" className="home-story-btn-back" onClick={prevPage}>
                <i className="fas fa-arrow-left" aria-hidden="true" />{t('common.back')}
              </button>
            ) : <span />}
            <button type="button" className="home-story-btn" disabled={!canAdvance} onClick={() => isLastPage ? onComplete(serializeAnswers(effectiveQuestions, answers)) : nextPage()}>
              {isLastPage ? t('common.submit') : t('common.next')}
              <i className={`fas fa-arrow-${isLastPage ? 'check' : 'right'}`} aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
